import { useState } from "react";
import QuoteCarousel from "@/components/QuoteCarousel";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { ArrowLeft, CheckCircle, Clock, AlertCircle, XCircle, Building2, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LogoMark } from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { UserButton } from "@clerk/react";

interface BankingStatus {
  verificationStatus: "pending" | "verified" | "failed";
  inviteSentAt: string | null;
  reminderSentAt: string | null;
}

interface Debt {
  id: number;
  creditor: string;
  balance: number;
  status: string;
  verificationStatus: string;
  queuePosition: number | null;
}

interface Pool {
  id: number;
  name: string;
  balance: number;
  threshold: number;
  status: string;
}

function BankingStatusBadge({ status }: { status: BankingStatus | undefined }) {
  if (!status) {
    return <Badge variant="outline" className="text-gray-500 border-gray-300 text-[10px]">Not invited</Badge>;
  }
  if (status.verificationStatus === "verified") {
    return <Badge className="bg-green-100 text-green-700 border-green-200 text-[10px]"><CheckCircle className="h-3 w-3 mr-1" />Verified</Badge>;
  }
  if (status.verificationStatus === "failed") {
    return <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px]"><XCircle className="h-3 w-3 mr-1" />Failed</Badge>;
  }
  if (status.inviteSentAt) {
    const daysSince = Math.floor((Date.now() - new Date(status.inviteSentAt).getTime()) / (1000 * 60 * 60 * 24));
    const label = daysSince === 0 ? "Today" : daysSince === 1 ? "1 day ago" : `${daysSince}d ago`;
    return (
      <div className="flex flex-col items-start gap-0.5">
        <Badge variant="outline" className="text-amber-700 border-amber-300 text-[10px]"><Clock className="h-3 w-3 mr-1" />Pending</Badge>
        <span className="text-[10px] text-muted-foreground">Invited {label}{status.reminderSentAt ? " · Reminded" : ""}</span>
      </div>
    );
  }
  return <Badge variant="outline" className="text-blue-600 border-blue-300 text-[10px]">Invite not sent</Badge>;
}

export default function PaymentDashboard() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "verified" | "pending" | "not_invited">("all");
  const [inviteDialog, setInviteDialog] = useState<{ debtId: number; creditorName: string } | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");

  const resendInviteMutation = useMutation({
    mutationFn: async ({ debtId, creditorEmail }: { debtId: number; creditorEmail: string }) => {
      const r = await fetch(`/api/admin/debts/${debtId}/resend-banking-invite`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ creditorEmail }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({ error: "Request failed" })) as { error: string };
        throw new Error(body.error ?? "Request failed");
      }
      return r.json() as Promise<{ message: string }>;
    },
    onSuccess: (data) => {
      toast({ title: "Invite re-sent", description: data.message });
      void queryClient.invalidateQueries({ queryKey: ["creditor-banking-status"] });
      setInviteDialog(null);
      setInviteEmail("");
    },
    onError: (err: Error) => {
      toast({ title: "Failed to re-send invite", description: err.message, variant: "destructive" });
    },
  });

  function openInviteDialog(debtId: number, creditorName: string) {
    setInviteEmail("");
    setInviteDialog({ debtId, creditorName });
  }

  function submitInvite() {
    if (!inviteDialog) return;
    resendInviteMutation.mutate({ debtId: inviteDialog.debtId, creditorEmail: inviteEmail });
  }

  const { data: pools } = useQuery<Pool[]>({
    queryKey: ["adminPools-payment"],
    queryFn: () => fetch("/api/admin/pools", { credentials: "include" }).then((r) => r.json()),
  });

  const { data: rawDebts } = useQuery<Debt[]>({
    queryKey: ["adminDebts-payment"],
    queryFn: () => fetch("/api/admin/debts", { credentials: "include" }).then((r) => r.json()),
  });

  const queuedDebts = (rawDebts ?? []).filter((d) => d.status === "queued" || d.status === "processing");
  const queuedDebtIds = queuedDebts.map((d) => d.id);

  const { data: bankingStatusMap } = useQuery<Record<number, BankingStatus>>({
    queryKey: ["creditor-banking-status", queuedDebtIds],
    queryFn: () => {
      if (queuedDebtIds.length === 0) return Promise.resolve({});
      return fetch(`/api/admin/creditor-banking-status?debtIds=${queuedDebtIds.join(",")}`, { credentials: "include" })
        .then((r) => r.json());
    },
    enabled: queuedDebtIds.length > 0,
  });

  const poolMap: Record<number, Pool> = {};
  for (const pool of pools ?? []) {
    poolMap[pool.id] = pool;
  }

  const verifiedCount = queuedDebts.filter((d) => bankingStatusMap?.[d.id]?.verificationStatus === "verified").length;
  const pendingCount = queuedDebts.filter((d) => bankingStatusMap?.[d.id] && bankingStatusMap[d.id].verificationStatus === "pending").length;
  const notInvitedCount = queuedDebts.filter((d) => !bankingStatusMap?.[d.id]).length;

  const filteredDebts = queuedDebts.filter((d) => {
    const banking = bankingStatusMap?.[d.id];
    const matchSearch = !search || d.creditor.toLowerCase().includes(search.toLowerCase());
    const matchStatus =
      statusFilter === "all" ||
      (statusFilter === "verified" && banking?.verificationStatus === "verified") ||
      (statusFilter === "pending" && banking && banking.verificationStatus === "pending") ||
      (statusFilter === "not_invited" && !banking);
    return matchSearch && matchStatus;
  });

  return (
    <div className="min-h-screen bg-background">
      <nav className="sticky top-0 z-50 border-b bg-slate-900 border-slate-700">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-slate-800" onClick={() => navigate("/admin")}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Admin
            </Button>
            <div className="flex items-center gap-2">
              <LogoMark size={28} />
              <span className="font-semibold text-white">Creditor Banking</span>
              <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-600 text-white tracking-wide">ADMIN</span>
            </div>
          </div>
          <UserButton />
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <div>
                  <div className="text-2xl font-bold text-green-700">{verifiedCount}</div>
                  <div className="text-xs text-muted-foreground">Bank Verified</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-amber-500" />
                <div>
                  <div className="text-2xl font-bold text-amber-600">{pendingCount}</div>
                  <div className="text-xs text-muted-foreground">Pending Verify</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-muted-foreground" />
                <div>
                  <div className="text-2xl font-bold">{notInvitedCount}</div>
                  <div className="text-xs text-muted-foreground">Not Invited</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Debt table */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <CardTitle className="flex items-center gap-2 text-base">
                <Building2 className="h-4 w-4 text-blue-600" />
                Queued & Processing Debts
              </CardTitle>
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Search creditor..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-8 w-48 text-sm"
                />
                <div className="flex rounded-md overflow-hidden border text-xs">
                  {(["all", "verified", "pending", "not_invited"] as const).map((f) => (
                    <button
                      key={f}
                      onClick={() => setStatusFilter(f)}
                      className={`px-3 py-1.5 font-medium transition-colors ${statusFilter === f ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
                    >
                      {f === "all" ? "All" : f === "not_invited" ? "Not Invited" : f.charAt(0).toUpperCase() + f.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredDebts.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No debts match your filters.</p>
            ) : (
              <div className="space-y-2">
                {filteredDebts.map((debt) => {
                  const banking = bankingStatusMap?.[debt.id];
                  return (
                    <div key={debt.id} className="flex items-center gap-4 py-2.5 px-3 rounded-lg border bg-muted/30 text-sm">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{debt.creditor}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-3 mt-0.5 flex-wrap">
                          <span>Balance: ${Number(debt.balance).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                          <span className="text-[10px] font-mono">#{String(debt.id).padStart(8, "0")}</span>
                          {debt.status === "processing" && <Badge className="text-[10px] bg-blue-100 text-blue-700 border-blue-200 py-0 px-1.5">Processing</Badge>}
                        </div>
                      </div>
                      <div className="shrink-0 flex items-center gap-3">
                        <BankingStatusBadge status={banking} />
                        {banking?.verificationStatus !== "verified" && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-xs gap-1"
                            onClick={() => openInviteDialog(debt.id, debt.creditor)}
                          >
                            <Send className="h-3 w-3" />
                            Re-send Invite
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="rounded-lg border bg-muted/20 p-4 text-sm text-muted-foreground">
          <p className="font-medium text-foreground mb-1">How the creditor banking flow works</p>
          <ul className="list-disc list-inside space-y-1">
            <li>When a debt enters <strong>queued</strong>, a banking invite is sent to the creditor</li>
            <li>Creditors click the link to submit their ACH banking details</li>
            <li>Once banking is verified, the debt is ready to be included in a payout batch</li>
          </ul>
        </div>
      </div>

      <QuoteCarousel />

      <Dialog open={!!inviteDialog} onOpenChange={(open) => { if (!open) { setInviteDialog(null); setInviteEmail(""); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Send Banking Invite</DialogTitle>
            <DialogDescription>
              Enter the verified email address for <strong>{inviteDialog?.creditorName}</strong>. This is where the banking setup link will be sent.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="invite-email">Creditor Email</Label>
            <Input
              id="invite-email"
              type="email"
              placeholder="creditor@example.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submitInvite(); }}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setInviteDialog(null); setInviteEmail(""); }}>Cancel</Button>
            <Button
              onClick={submitInvite}
              disabled={resendInviteMutation.isPending || !inviteEmail.trim()}
            >
              {resendInviteMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Send Invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
